import { Product } from '../../shop-cart/interfaces/shop-cart.interface';
import { ProductCardData } from '../../../product-card/product-card-display';
import { getCopyRightYear, getFormatText, getPrice, getProductLink, isSEG } from '../utils/cart-list-util';
import { productTitle } from '../../../../common/utils/util';
import { isAleksSharpenCompanionBundle } from '../../../../common/utils/aleks-sharpen-bundle-util';
import { wasFreeTrialConverted } from '../../../free-trial/utils/free-trial-conversion.util';
import { wasFreeTrialReplaced } from '../../../free-trial/utils/free-trial-replacement.util';

/**
 * Maps the Magento cart products to the products being displayed.
 */
export default function mapMagentoProductsToCardProducts(products: Product[], store?: string): ProductCardData[] {
  return products.map((product) => ({
    id: '',
    kind: 'ProductCardData',
    programSourceId: product.productIdentifier.programSourceId,
    programSourceName: product.productIdentifier.programSourceName,
    title: productTitle(product),
    optimizedUniqueTitle: '',
    image: product.productMetadata.imageUrl,
    url: getProductLink(product, store),
    copyRightYear: getCopyRightYear(product),
    businessUnit: product.productIdentifier.businessUnit,
    format: getFormatText(product),
    formatTypeDesc: product.productMetadata?.formatTypeDesc,
    instructorOnly: false,
    store: '',
    offerPricingId: product.productIdentifier.offerPricingId,
    isbn: product.productIdentifier.isbn10,
    isbn13: product.productIdentifier.isbn13,
    mhid: product.productIdentifier.mhid,
    edition: product.productMetadata?.edition,
    editionOrRelease: product.productMetadata?.editionOrRelease,
    netPrice: getPrice(product),
    totalPrice: getPrice(product) * product.quantity,
    listPrice: product.priceInfo.listPrice,
    isDiscountedBundle: isDiscountedBundlePrice(product),
    bundleDiscountedPrice: 0,
    contributors: product.productMetadata.contributors,
    duration: product.productMetadata.duration,
    durationPeriod: product.productMetadata.durationPeriod,
    description: '',
    gradeLevels: isSEG(product) ? product.productMetadata.grades : undefined,
    isPublished: product.availabilityInfo?.available,
    quantity: product.quantity,
    isVirtual: product.productMetadata.virtual,
    cardType: product.productMetadata.cardType,
    errorCode: product.availabilityInfo?.errorCode,
    errorMessage: product.availabilityInfo?.errorMessage,
    looseLeafAvailable: product.productIdentifier.looseLeafAvailable,
    hasEbook: product.productIdentifier.hasEbook,
    isConnectSharpenBundle: product.productIdentifier.isConnectSharpenBundle,
    isSharpenCompanion: product.productIdentifier.isSharpenCompanion,
    isSharpenAdvantage: product.productIdentifier.isSharpenAdvantage,
    term: product.productIdentifier.term,
    countryCode: product.priceInfo.countryCode,
    currencyCode: product.priceInfo.currencyCode,
    isEvergreen: product.productMetadata.isEvergreen,
    offerType: product.productMetadata.offerType,
    offerBranding: product.productMetadata.offerBranding,
    application: product.productMetadata.application,
    itemType: product.productMetadata?.itemType,
    flow: product.productMetadata?.flow,
    isFreeTrial: product.freeTrialInfo?.isFreeTrial,
    isFreeTrialReplaced: wasFreeTrialReplaced(product),
    isConvertedFromFreeTrial: wasFreeTrialConverted(product.productIdentifier?.isbn10),
    freeTrialEndDate: product.freeTrialInfo?.endDate,
    freeTrialStartDate: product.freeTrialInfo?.startDate,
    freeTrialSubscriptionAmount: product.freeTrialInfo?.subscriptionAmount,
    freeTrialSubscriptionUnitPrice: product.freeTrialInfo?.subscriptionUnitPrice,
    durationType: product.productMetadata.durationType,
    ...(product.createCustomProductMetadata && {
      courseNumber: product.createCustomProductMetadata.courses?.[0]?.courseNo,
      createCoverImages: product.createCustomProductMetadata.createCoverImages,
      department: product.createCustomProductMetadata.department,
      instructorNames: product.createCustomProductMetadata.instructorNames,
      schoolName: product.createCustomProductMetadata.school?.name,
      supplements: product.createCustomProductMetadata.supplements,
    }),
    metaOfferContentsMetadata: product.productMetadata?.metaOfferContentsMetadata,
    isAleksSharpenCompanionBundle: isAleksSharpenCompanionBundle(product.productMetadata?.offerType, product.productIdentifier?.isSharpenCompanion),
  }));
}

/**
  If magento didn't return it later on directly on the API, we need to update it for SEG Admin Users as they pay the netPrice
  Then, in that scenario the list will be always different than the customPrice here.

  We are checking if the offerPricingId exists, as it will limit it to edoms B2C scope of just HE products.
 */
function isDiscountedBundlePrice(product) {
  return product.priceInfo.listPrice !== product.priceInfo.customPrice && !!product.productIdentifier.offerPricingId;
}
